import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type FollowedTeacher = {
  id: string;
  display_name: string;
  avatar_url: string | null;
  /** id de la ligne teacher_follows */
  followId: string;
};

type FollowRow = {
  id: string;
  teacher_id: string;
  teacher: { id: string; display_name: string; avatar_url: string | null } | null;
};

/** Liste les profs suivis (unilatéral) par `studentId`. */
export function useFollowedTeachers(studentId: string) {
  return useQuery({
    queryKey: ['followed-teachers', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<FollowedTeacher[]> => {
      const { data, error } = await supabase
        .from('teacher_follows')
        .select(
          `id, teacher_id, teacher:profiles!teacher_follows_teacher_id_fkey ( id, display_name:visible_name, avatar_url )`,
        )
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as FollowRow[])
        .map<FollowedTeacher | null>((row) => {
          if (!row.teacher) return null;
          return {
            id: row.teacher.id,
            display_name: row.teacher.display_name,
            avatar_url: row.teacher.avatar_url,
            followId: row.id,
          };
        })
        .filter((t): t is FollowedTeacher => t !== null);
    },
  });
}

export function useFollowTeacher(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teacherId: string) => {
      const { error } = await supabase.rpc('follow_teacher', { p_teacher_id: teacherId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followed-teachers', studentId] });
    },
  });
}

export function useUnfollowTeacher(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teacherId: string) => {
      const { error } = await supabase.rpc('unfollow_teacher', { p_teacher_id: teacherId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followed-teachers', studentId] });
    },
  });
}
